import { styled } from "@linaria/react";
import React from "react";
import type { User } from "../../../api/useUser";
import type { UserProfil, UserRole } from "../../../api/useUsers";
import { canUserDo } from "diagnostics-domain/canUserDo";
import useToasts from "@ui/Toast/useToasts";
import t from "../../../i18n";
import { color, easingBounce, easingBounceMs, fonts, radius } from "@ui";
import LinkMemo from "@ui/Link";
import Menu from "@ui/Menu";
import type { RoleOption } from "./GlobalRolesOptions";
import RolesOptions from "./GlobalRolesOptions";
import updateGlobalRole from "./updateGlobalRole";
import { getGlobalRoleName, getGlobalRoleOptionsBase } from "./globalRoleOptions";

const StyledMenu = styled(Menu)`
  font: ${fonts.body2SemiBold};
  min-width: 300px;
`;

const SelectLink = styled(LinkMemo)`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  color: ${color.carbon900};
  font: ${fonts.body1};
  padding: 8px;
  border-radius: ${radius};
  min-width: 80px;
  flex-shrink: 0;

  :hover {
    background-color: ${color.titanium100};
  }
  &.opened {
    box-shadow: inset 0 0 0 1px ${color.titanium100};
  }
  ::after {
    margin-left: 10px;
    display: inline-block;
    content: "";
    width: 0.55em;
    height: 0.55em;
    border-top: 1px solid currentColor;
    border-right: 1px solid currentColor;
    transition: transform ${easingBounceMs}ms ${easingBounce};
    transform: rotate(135deg);
    transform-origin: 65% 35%;
    margin-right: 0.3em;
  }
  &.opened::after {
    transform: rotate(315deg);
  }
`;

interface P {
  user: User;
  userProfil: UserProfil;
}

function GlobalRoleMenu({ user, userProfil }: P) {
  const { addToast } = useToasts();
  const canAssignApplicationPermissions = canUserDo(user, "ASSIGN_GLOBAL_ROLE");
  const currentRole = userProfil.roles.global;

  const options: RoleOption[] = React.useMemo(
    () =>
      getGlobalRoleOptionsBase().map(option => ({
        ...option,
        disabled: !canAssignApplicationPermissions,
      })),
    [canAssignApplicationPermissions],
  );

  const label = React.useMemo(() => {
    const option = options.find(option => option.value === userProfil.roles.global);
    return option?.label ?? t("users.table.row.menu.roles.no_role");
  }, [options, userProfil.roles.global]);

  const handleRoleClick = React.useCallback(
    async (globalRole: UserRole) => {
      const currentRole = userProfil.roles.global;
      const newRole = currentRole === globalRole ? null : globalRole;

      try {
        await updateGlobalRole(userProfil.id, newRole);
        const messageVariables = {
          userName: userProfil.name ?? userProfil.login,
          role: getGlobalRoleName(globalRole),
        };
        const message = newRole
          ? t("users.table.row.roles.application.toast_new_role", messageVariables)
          : t("users.table.row.roles.application.toast_no_role", {
              userName: messageVariables.userName,
            });

        addToast("success", message);
      } catch {
        addToast("error", t("errors.backend_error"));
      }
    },
    [addToast, userProfil.id, userProfil.name, userProfil.login, userProfil.roles.global],
  );

  return (
    <StyledMenu
      placement="auto-end"
      arrowWidth={0}
      overlay={
        <RolesOptions options={options} currentSelection={currentRole} onClick={handleRoleClick} />
      }
    >
      <SelectLink>{label ? label : t("users.table.row.menu.roles.no_role")}</SelectLink>
    </StyledMenu>
  );
}

export default React.memo(GlobalRoleMenu);
